import React, { useState, useEffect, useRef } from 'react';
import '../styles.css';

const Stopwatch = ({ isOpen, onClose, currentLapCount, onAddLaps, existingLaps = [] }) => {
  const [time, setTime] = useState(0);
  const [lapStartTime, setLapStartTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [laps, setLaps] = useState([]);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const intervalRef = useRef(null);
  const wakeLockRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setLaps([]);
      setTime(0);
      setLapStartTime(0);
      setIsRunning(false);
    }
  }, [isOpen]);

  // Handle page visibility changes - stop stopwatch if page becomes hidden
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && isRunning) {
        setIsRunning(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isRunning]);

  useEffect(() => {
    if (isRunning) {
      intervalRef.current = setInterval(() => {
        setTime(prevTime => prevTime + 10);
      }, 10);

      // Request wake lock to keep screen on
      if ('wakeLock' in navigator) {
        navigator.wakeLock.request('screen').then(lock => {
          wakeLockRef.current = lock;
          lock.addEventListener('release', () => {
            wakeLockRef.current = null;
          });
        }).catch(err => {
          console.log('Wake lock request failed:', err);
        });
      }
    } else {
      clearInterval(intervalRef.current);
      // Release wake lock
      if (wakeLockRef.current) {
        wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    }
    return () => {
      clearInterval(intervalRef.current);
      if (wakeLockRef.current) {
        wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    };
  }, [isRunning]);

  const formatTime = (milliseconds) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const ms = Math.floor((milliseconds % 1000) / 10);
    return `${minutes}:${seconds.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;
  };

  const parseLapTime = (formatted) => {
    if (!formatted || !formatted.includes(':')) return Infinity;
    // Remove asterisk if present (generated lap marker)
    const cleanFormatted = formatted.replace('*', '');
    const parts = cleanFormatted.split(':');
    const minutes = parseInt(parts[0]) || 0;
    const secondsMs = parts[1].split('.');
    const seconds = parseInt(secondsMs[0]) || 0;
    const ms = parseInt(secondsMs[1]) || 0;
    return (minutes * 60 * 1000) + (seconds * 1000) + (ms * 10);
  };

  const formatTimeDifference = (current, best) => {
    if (!current || !best || current === best) return '';
    const diff = parseLapTime(current) - parseLapTime(best);
    if (diff === 0) return '';
    const seconds = Math.floor(Math.abs(diff) / 1000);
    const milliseconds = Math.abs(diff) % 1000;
    const sign = diff > 0 ? '+' : '-';
    return `${sign}${seconds}.${milliseconds.toString().padStart(3, '0')}`;
  };

  const handleStart = () => {
    setIsRunning(true);
    setLapStartTime(time);
  };

  const handleStop = () => {
    setIsRunning(false);
  };

  const handleLap = () => {
    if (isRunning) {
      const lapTime = formatTime(time - lapStartTime);
      setLaps(prevLaps => [...prevLaps, lapTime]);
      setLapStartTime(time);
    }
  };

  const handleReset = () => {
    setShowResetConfirm(true);
  };

  const confirmReset = (clearAll) => {
    if (clearAll) {
      setLaps([]);
    }
    setTime(0);
    setLapStartTime(0);
    setIsRunning(false);
    setShowResetConfirm(false);
  };

  const handleClose = () => {
    if (laps.length > 0) {
      onAddLaps(laps);
    }
    onClose();
  };

  // Calculate best lap from both existing laps and current stopwatch laps
  const allLaps = [...existingLaps, ...laps];
  const bestLap = allLaps.length > 0 ? allLaps.reduce((best, current) =>
    parseLapTime(current) < parseLapTime(best) ? current : best
  ) : null;

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="stopwatchmodal stopwatch-modal">
        <div className="modal-header">
          <h2>Stopwatch</h2>
          <button onClick={handleClose} className="close-button-dark">✕</button>
        </div>
        {isRunning && (
          <div className="stopwatch-warning">
            ⚠️ Stopwatch will stop if you navigate away from this page
          </div>
        )}
        <div className="stopwatch-display">
          <div className="stopwatch-lap-time">{formatTime(time - lapStartTime)}</div>
          <div className="stopwatch-time">{formatTime(time)}</div>
          <div className="stopwatch-laps">Laps: {currentLapCount + laps.length}</div>
          {bestLap && (
            <div className="stopwatch-best-lap">Best Lap: {bestLap}</div>
          )}
        </div>
        <div className="stopwatch-lap-list">
          {laps.slice().reverse().map((lap, index) => (
            <div key={laps.length - 1 - index} className="stopwatch-lap-item">
              Lap {currentLapCount + laps.length - index}: {lap}
              {bestLap && lap !== bestLap && (
                <span className="lap-time-difference"> {formatTimeDifference(lap, bestLap)}</span>
              )}
            </div>
          ))}
        </div>
        <div className="stopwatch-buttons">
          {!isRunning ? (
            <button onClick={handleStart} className="btn btn-primary">Start</button>
          ) : (
            <button onClick={handleStop} className="btn btn-secondary">Stop</button>
          )}
          <button onClick={handleReset} className="btn btn-danger">Reset</button>
          <button onClick={handleLap} className="btn btn-info" disabled={!isRunning}>Lap</button>
        </div>
        {showResetConfirm && (
          <div className="reset-confirm-overlay">
            <div className="reset-confirm-dialog">
              <p>Do you want to clear all entered laps or continue?</p>
              <button onClick={() => confirmReset(true)} className="btn btn-danger">Clear All</button>
              <button onClick={() => confirmReset(false)} className="btn btn-secondary">Continue</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Stopwatch;